import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { mealsAPI } from '../../services/api';

interface MealFoodItem {
  foodName: string;
  servingSize: number;
  servingMeasure: string;
  foodType: 'Carb' | 'Protein' | 'Fat' | 'Supplement' | 'Ingredient';
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  recipe?: string;
  completed?: boolean;
  completedAt?: string;
}

interface MealEntry {
  mealNumber: number;
  mealLabel?: string;
  notes?: string;
  foodItems: MealFoodItem[];
}

interface MealPlan {
  id: string;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  endDate: string;
  meals: MealEntry[];
  overallFeedback?: string;
  overallRating?: number;
  completedDate?: string;
}

const foodTypeColor: Record<string, string> = {
  Carb:       'bg-yellow-100 text-yellow-700',
  Protein:    'bg-blue-100 text-blue-700',
  Fat:        'bg-orange-100 text-orange-700',
  Supplement: 'bg-purple-100 text-purple-700',
  Ingredient: 'bg-gray-100 text-gray-600',
};

const MealDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [planData, setPlanData] = useState<MealPlan | null>(null);
  const [expandedMeals, setExpandedMeals] = useState<Set<number>>(new Set([0]));
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['meal-plan', id],
    queryFn: () => mealsAPI.getById(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (data?.data) {
      setPlanData(data.data);
      setFeedback(data.data.overallFeedback || '');
      setRating(data.data.overallRating || 0);
    }
  }, [data]);

  const toggleMeal = (idx: number) => {
    const next = new Set(expandedMeals);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpandedMeals(next);
  };

  // Toggle individual food item
  const toggleFoodItem = useMutation({
    mutationFn: ({ mealIndex, foodIndex, completed }: { mealIndex: number; foodIndex: number; completed: boolean }) =>
      mealsAPI.markFoodItem(id!, { mealIndex, foodIndex, completed }),
    onSuccess: (res) => {
      if (planData && res.data?.mealPlan?.meals) {
        setPlanData({ ...planData, meals: res.data.mealPlan.meals });
      }
    },
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to update food item'),
  });

  const handleToggleFood = (mealIndex: number, foodIndex: number) => {
    if (!planData || isCompleted) return;
    const current = planData.meals[mealIndex].foodItems[foodIndex].completed ?? false;
    // Optimistic update
    const updated = { ...planData };
    updated.meals[mealIndex].foodItems[foodIndex].completed = !current;
    setPlanData({ ...updated });
    toggleFoodItem.mutate({ mealIndex, foodIndex, completed: !current });
  };

  // Complete the plan
  const completePlan = useMutation({
    mutationFn: () => mealsAPI.complete(id!, {
      overallFeedback: feedback,
      overallRating: rating || undefined,
      completedDate: new Date().toISOString(),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-meal-plans'] });
      queryClient.invalidateQueries({ queryKey: ['meal-plan', id] });
      navigate('/client/meals');
    },
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to complete meal plan'),
  });

  const handleComplete = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await completePlan.mutateAsync();
    } finally {
      setIsSubmitting(false);
      setShowFeedbackModal(false);
    }
  };

  const isCompleted = planData?.status === 'COMPLETED';

  const getMealTotals = (items: MealFoodItem[]) => ({
    calories: items.reduce((s, f) => s + (f.calories || 0), 0),
    protein:  items.reduce((s, f) => s + (f.protein  || 0), 0),
    carbs:    items.reduce((s, f) => s + (f.carbs    || 0), 0),
    fat:      items.reduce((s, f) => s + (f.fat      || 0), 0),
  });

  const getDayTotals = () => {
    const all = planData?.meals.flatMap(m => m.foodItems) || [];
    return getMealTotals(all);
  };

  const formatDate = (d: string) => {
    const dt = new Date((d.includes('T') ? d.split('T')[0] : d) + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Meal plan not found</p>
          <button onClick={() => navigate('/client/meals')} className="text-blue-600 hover:text-blue-800">
            Back to Meals
          </button>
        </div>
      </div>
    );
  }

  const dayTotals = getDayTotals();

  return (
    <div className="min-h-screen bg-gray-50 -mt-6">

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-lg sm:rounded-lg">
            <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Complete Meal Plan</h3>
              <button onClick={() => setShowFeedbackModal(false)}>
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-4">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                        rating >= star ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {star}
                    </button>
                  ))}
                </div>
              </div>
              {/* Feedback */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  rows={4}
                  placeholder="How did this meal plan work for you?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex space-x-3">
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
              >
                {isSubmitting ? 'Saving...' : 'Complete Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-16 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button onClick={() => navigate('/client/meals')} className="p-1 text-gray-600 hover:text-gray-900">
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{planData.name}</h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!isCompleted ? (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="flex items-center space-x-1 bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 text-sm"
              >
                <CheckIcon className="h-4 w-4" />
                <span>Complete</span>
              </button>
            ) : (
              <div className="flex items-center space-x-1 text-green-600 text-sm">
                <CheckCircleIcon className="h-4 w-4" />
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">

        {/* Plan Info */}
        <div className="bg-white rounded-lg shadow-sm p-3 space-y-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Date Range</p>
              <p className="font-medium">{formatDate(planData.startDate)} – {formatDate(planData.endDate)}</p>
            </div>
            <div>
              <p className="text-gray-500">Status</p>
              <p className="font-medium">{planData.status}</p>
            </div>
          </div>
          {planData.description && (
            <p className="text-sm text-gray-600">{planData.description}</p>
          )}
        </div>

        {/* Daily Macro Totals */}
        <div className="bg-white rounded-lg shadow-sm p-3">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Totals</h3>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { label: 'Cal',  value: Math.round(dayTotals.calories), color: 'text-gray-900' },
              { label: 'P',    value: `${Math.round(dayTotals.protein)}g`,  color: 'text-blue-600'   },
              { label: 'C',    value: `${Math.round(dayTotals.carbs)}g`,    color: 'text-yellow-600' },
              { label: 'F',    value: `${Math.round(dayTotals.fat)}g`,      color: 'text-orange-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 rounded-lg py-2">
                <p className={`text-base font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Meals */}
        {planData.meals
          .sort((a, b) => a.mealNumber - b.mealNumber)
          .map((meal, mealIdx) => {
            const totals = getMealTotals(meal.foodItems);
            const isOpen = expandedMeals.has(mealIdx);
            return (
              <div key={mealIdx} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Meal Header */}
                <div
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleMeal(mealIdx)}
                >
                  <div className="flex items-center space-x-2">
                    <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        Meal {meal.mealNumber}{meal.mealLabel ? ` — ${meal.mealLabel}` : ''}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {Math.round(totals.calories)} cal · {meal.foodItems.length} item{meal.foodItems.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    <span className="text-blue-600">{Math.round(totals.protein)}g P</span>
                    {' · '}
                    <span className="text-yellow-600">{Math.round(totals.carbs)}g C</span>
                    {' · '}
                    <span className="text-orange-600">{Math.round(totals.fat)}g F</span>
                  </div>
                </div>

                {/* Meal Content */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {meal.notes && (
                      <p className="px-3 pt-2 text-xs text-gray-500 italic">{meal.notes}</p>
                    )}
                    <div className="divide-y divide-gray-50">
                      {meal.foodItems.map((food, foodIdx) => (
                        <div
                          key={foodIdx}
                          className={`flex items-start justify-between px-3 py-2 cursor-pointer transition-colors ${
                            food.completed ? 'bg-green-50' : 'hover:bg-gray-50'
                          } ${isCompleted ? 'cursor-default' : ''}`}
                          onClick={() => handleToggleFood(mealIdx, foodIdx)}
                        >
                          <div className="flex items-start space-x-2 flex-1 min-w-0">
                            <div className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                              food.completed
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300 bg-white'
                            }`}>
                              {food.completed && <CheckIcon className="h-3 w-3 text-white" />}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-medium ${food.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                {food.foodName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {food.servingSize} {food.servingMeasure}
                              </p>
                              {food.recipe && (
                                <p className="text-xs text-gray-400 mt-0.5 italic truncate">{food.recipe}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-2 text-right">
                            <span className={`inline-flex text-xs px-1.5 py-0.5 rounded ${foodTypeColor[food.foodType] || 'bg-gray-100 text-gray-600'}`}>
                              {food.foodType}
                            </span>
                            {food.calories != null && (
                              <p className="text-xs text-gray-500 mt-0.5">{food.calories} cal</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {/* Completed feedback display */}
        {isCompleted && planData.overallFeedback && (
          <div className="bg-green-50 rounded-lg p-3">
            <h3 className="text-sm font-semibold text-green-800 mb-1">Your Notes</h3>
            <p className="text-sm text-green-700">{planData.overallFeedback}</p>
            {planData.overallRating && (
              <p className="text-xs text-green-600 mt-1">Rating: {planData.overallRating}/5</p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default MealDetail;
